import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, Building2, MapPin,
  Globe, Sparkles, Rocket,
} from "lucide-react";
import { onboardingComplete } from "@/lib/api";
import { toast } from "sonner";

/** 5-step onboarding wizard — /onboarding
 *  1. Legal / display name
 *  2. Category + description
 *  3. Website + email + languages
 *  4. Primary location (address, phone, hours, service area)
 *  5. Review + submit → runs Local Visibility audit + redirects to dashboard
 */

const STEPS = [
  { key: "identity", title: "Business identity", icon: Building2 },
  { key: "category", title: "Category & description", icon: Sparkles },
  { key: "web", title: "Web presence & languages", icon: Globe },
  { key: "location", title: "Primary location", icon: MapPin },
  { key: "review", title: "Review & launch", icon: Rocket },
];

export default function OnboardingWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    legal_name: "", display_name: "",
    primary_category: "", description: "",
    website: "", email: "", languages: "en",
    location_label: "", address: "", phone: "", hours: "", service_area: "",
    run_local_visibility: true,
  });

  useEffect(() => {
    document.title = "Onboarding — WebsiteTech.pro";
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const canNext = () => {
    if (step === 0) return form.legal_name.trim().length >= 2;
    if (step === 3) return form.location_label.trim().length >= 2;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        identity: {
          legal_name: form.legal_name.trim(),
          display_name: form.display_name.trim() || undefined,
          primary_category: form.primary_category.trim() || undefined,
          description: form.description.trim() || undefined,
          website: form.website.trim() || undefined,
          email: form.email.trim() || undefined,
          languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        },
        primary_location: {
          label: form.location_label.trim(),
          address: form.address.trim() || undefined,
          phone: form.phone.trim() || undefined,
          hours: form.hours.trim() || undefined,
          service_area: form.service_area
            .split(",").map((s) => s.trim()).filter(Boolean),
        },
        run_local_visibility: form.run_local_visibility,
      };
      const r = await onboardingComplete(payload);
      toast.success("Onboarding complete — welcome to WebsiteTech.pro");
      nav(`/dashboard?onboarded=1&audit=${r.lv_audit_id || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed. Sign in first.");
    } finally { setBusy(false); }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="onboarding-page">
      <Navbar />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Rocket className="h-4 w-4" /> Onboarding
          </div>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Five quick steps to launch your growth stack.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            We'll set up your canonical business record, capture your first
            location, and run a Local Visibility audit so day one already ships
            with a scorecard.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === step;
              const done = i < step;
              return (
                <div key={s.key} className="flex items-center gap-1">
                  <div
                    className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${active ? "border-orange-400 bg-orange-50 text-orange-800" : done ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500"}`}
                    data-testid={`onboarding-step-${s.key}`}
                  >
                    {done ? <CheckCircle2 className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                    {i + 1}. {s.title}
                  </div>
                  {i < STEPS.length - 1 && <div className="h-px w-4 bg-slate-200" />}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-10">
        <Card className="border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2 text-slate-500">
            <StepIcon className="h-4 w-4" />
            <span className="text-sm font-semibold">Step {step + 1} of {STEPS.length}</span>
          </div>
          <div className="mt-4 space-y-4">
            {step === 0 && (
              <>
                <Field label="Legal business name*" value={form.legal_name}
                  onChange={(v) => set("legal_name", v)} testid="ob-legal-name" />
                <Field label="Display name (public)" value={form.display_name}
                  onChange={(v) => set("display_name", v)} testid="ob-display-name" />
              </>
            )}
            {step === 1 && (
              <>
                <Field label="Primary category" value={form.primary_category}
                  onChange={(v) => set("primary_category", v)}
                  placeholder="Plumber · Restaurant · Dental clinic · …"
                  testid="ob-category" />
                <label className="block text-xs font-semibold text-slate-700">
                  Short description
                  <Textarea value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    className="mt-1"
                    rows={4}
                    placeholder="One paragraph that explains what you do best."
                    data-testid="ob-description"
                  />
                </label>
              </>
            )}
            {step === 2 && (
              <>
                <Field label="Website URL" value={form.website}
                  onChange={(v) => set("website", v)}
                  placeholder="https://…" testid="ob-website" />
                <Field label="Public email" value={form.email}
                  onChange={(v) => set("email", v)}
                  placeholder="hello@example.com" testid="ob-email" />
                <Field label="Languages (comma separated)" value={form.languages}
                  onChange={(v) => set("languages", v)}
                  placeholder="en, hi, ml" testid="ob-languages" />
              </>
            )}
            {step === 3 && (
              <>
                <Field label="Location label*" value={form.location_label}
                  onChange={(v) => set("location_label", v)}
                  placeholder="Head office · Downtown branch"
                  testid="ob-location-label" />
                <Field label="Address" value={form.address}
                  onChange={(v) => set("address", v)} testid="ob-address" />
                <Field label="Phone" value={form.phone}
                  onChange={(v) => set("phone", v)} testid="ob-phone" />
                <Field label="Hours" value={form.hours}
                  onChange={(v) => set("hours", v)}
                  placeholder="Mon–Fri 9–6, Sat 10–2" testid="ob-hours" />
                <Field label="Service area (comma separated)"
                  value={form.service_area}
                  onChange={(v) => set("service_area", v)}
                  placeholder="Kollam, Trivandrum, Kochi" testid="ob-service-area" />
              </>
            )}
            {step === 4 && (
              <div className="space-y-2 text-sm text-slate-700" data-testid="ob-review">
                <div><b>{form.legal_name || "—"}</b>
                  {form.display_name && ` (${form.display_name})`}</div>
                <div className="text-slate-500">
                  {form.primary_category || "No category"} ·
                  {form.website ? ` ${form.website}` : " no website"} ·
                  {form.email ? ` ${form.email}` : " no email"}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="font-semibold">{form.location_label || "—"}</div>
                  <div className="text-xs text-slate-500">
                    {form.address || "no address"} · {form.phone || "no phone"} · {form.hours || "hours n/a"}
                  </div>
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs">
                  <input type="checkbox"
                    checked={form.run_local_visibility}
                    onChange={(e) => set("run_local_visibility", e.target.checked)}
                    data-testid="ob-run-lv" />
                  Run a Local Visibility audit on completion
                </label>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={step === 0 || busy}
              data-testid="ob-back"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext() || busy}
                className="bg-orange-600 text-white hover:bg-orange-700"
                data-testid="ob-next"
              >
                Continue <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="ob-submit"
              >
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Rocket className="mr-1 h-4 w-4" />}
                Launch my stack
              </Button>
            )}
          </div>
        </Card>
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block text-xs font-semibold text-slate-700">
      {label}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1"
        placeholder={placeholder}
        data-testid={testid}
      />
    </label>
  );
}
